import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Alert,
  Box,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

function AdminDashboard() {
  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]); // For local DB organizations
  const [epicOrganizations, setEpicOrganizations] = useState([]); // For onboarding from EPIC

  // State variables for practitioner from EPIC organization for  onboarding
  const [
    openPractitionerOnboardingDialog,
    setOpenPractitionerOnboardingDialog,
  ] = useState(false);
  const [selectedOrgForPractitioners, setSelectedOrgForPractitioners] =
    useState("");
  const [epicPractitioners, setEpicPractitioners] = useState([]);
  const [selectedPractitioner, setSelectedPractitioner] = useState("");

  const [selectedOrganization, setSelectedOrganization] = useState("");
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [openOnboardingDialog, setOpenOnboardingDialog] = useState(false);

  const [healthcareProviders, setHealthcareProviders] = useState([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [providerSearchQuery, setProviderSearchQuery] = useState("");

  const [openAddUserDialog, setOpenAddUserDialog] = useState(false);
  const [openUserDetailsDialog, setOpenUserDetailsDialog] = useState(false);

  const [openProviderDialog, setOpenProviderDialog] = useState(false);
  const [newUser, setNewUser] = useState({
    firstName: "",
    lastName: "",
    email: "",
    role: "",
    healthcareProvider: "",
  });
  const [newProvider, setNewProvider] = useState({
    name: "",
    tax_id: "",
    contact: { email: "", phone: "" },
  });
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const navigate = useNavigate();

  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);

  const [patients, setPatients] = useState([]); // Stores patients by healthcare provider ID
  const [selectedPatient, setSelectedPatient] = useState(null); // ✅ Fixed useState initialization
  const [openPatientDialog, setOpenPatientDialog] = useState(false); // ✅ Fixed useState initialization

  const [openGroomingDialog, setOpenGroomingDialog] = useState(false);
  const [openCGMDialog, setOpenCGMDialog] = useState(false);
  const [groomingData, setGroomingData] = useState(null);
  const [cgmData, setCGMData] = useState(null);

  useEffect(() => {
    fetchUsers();
    fetchOrganizations();
    fetchHealthcareProviders();
  }, []);

  // New fetch specifically for EPIC onboarding
  const fetchEpicOrganizations = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("You must be logged in to fetch organizations.");
        console.error("❌ No token found in localStorage.");
        return;
      }

      const res = await axios.get(
        "https://app.metabolytics.ai/simulator-backend/epic/fetch-organizations",
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Get available organizations
      const availableOrgs = res.data.filter((org) => !org.onboarded);
      setEpicOrganizations(availableOrgs);
    } catch (err) {
      console.error(
        "❌ Error fetching EPIC organizations:",
        err.response?.data || err.message
      );
      alert("Failed to fetch organizations from EPIC");
    }
  };

  // Get  practitioners from an organization  in EPIC for onboarding
  const handleOnboardPractitioners = async () => {
    if (!selectedOrgForPractitioners) {
      alert("Please select an organization first.");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        "https://app.metabolytics.ai/simulator-backend/epic/onboard-practitioners",
        { organizationId: selectedOrgForPractitioners },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      setOpenPractitionerOnboardingDialog(false);
      alert(
        "All practitioners from the organization have been onboarded successfully!"
      );
      fetchUsers(); // Refresh the users list
    } catch (error) {
      console.error("Error onboarding practitioners:", error);
      alert("Failed to onboard practitioners.");
    }
  };

  // Fetch function  for EPIC onboarding
  const fetchEpicPractitioners = async () => {
    if (!selectedOrgForPractitioners) {
      console.warn("⚠️ No organization selected for fetching practitioners.");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("You must be logged in to fetch practitioners.");
        console.error("❌ No token found in localStorage.");
        return;
      }

      const res = await axios.get(
        `https://app.metabolytics.ai/simulator-backend/epic/fetch-practitioners/${selectedOrgForPractitioners}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const availablePractitioners = res.data.filter((prac) => !prac.onboarded);
      setEpicPractitioners(availablePractitioners);
    } catch (err) {
      console.error(
        "❌ Error fetching EPIC practitioners:",
        err.response?.data || err.message
      );
      alert("Failed to fetch practitioners from EPIC");
    }
  };

  // Use both fetches when opening the onboarding dialog
  const handleOpenOnboardingDialog = () => {
    fetchEpicOrganizations(); // Fetch EPIC organizations for onboarding
    setOpenOnboardingDialog(true);
  };

  const handleOnboard = async () => {
    if (!selectedOrganization) {
      alert("Please select an organization to onboard.");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        "https://app.metabolytics.ai/simulator-backend/epic/onboard-organization",
        { organizationId: selectedOrganization },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      setOpenOnboardingDialog(false);
      alert("Organization onboarded successfully!");
      fetchOrganizations(); // ✅ Refresh the organizations list immediately after onboarding
    } catch (error) {
      console.error("Error onboarding organization:", error);
      alert("Failed to onboard organization.");
    }
  };

  const handleOpenPractitionerOnboardingDialog = () => {
    fetchEpicPractitioners();
    setOpenPractitionerOnboardingDialog(true);
  };

  const handleOnboardPractitioner = async () => {
    if (!selectedPractitioner) {
      alert("Please select a practitioner to onboard.");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        "https://app.metabolytics.ai/simulator-backend/epic/onboard-practitioner",
        { practitionerId: selectedPractitioner },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      setOpenPractitionerOnboardingDialog(false);
      alert("Practitioner onboarded successfully!");
      fetchUsers(); // Refresh the users list
    } catch (error) {
      console.error("Error onboarding practitioner:", error);
      alert("Failed to onboard practitioner.");
    }
  };

  const handleViewGrooming = async (patient) => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `https://app.metabolytics.ai/simulator-backend/patients/api/grooming?name=${encodeURIComponent(
          patient.name
        )}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setGroomingData(res.data);
      setOpenGroomingDialog(true);
    } catch (err) {
      console.error("Error fetching grooming data:", err);
    }
  };

  const handleViewCGM = async (patient) => {
    try {
      const token = localStorage.getItem("token");

      console.log(
        `📡 Fetching CGM AI analysis for patient: ${patient.name}...`
      );

      const res = await axios.get(
        `https://app.metabolytics.ai/simulator-backend/patients/api/cgm-analysis?name=${encodeURIComponent(
          patient.name
        )}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.data.analysis) {
        console.log("✅ AI Analysis Data:", res.data.analysis);
        setCGMData(res.data.analysis);
        setOpenCGMDialog(true);
      } else {
        console.warn("⚠️ No AI analysis found for patient.");
        alert("No AI-generated CGM analysis available for this patient.");
      }
    } catch (err) {
      console.error(
        "❌ Error fetching AI CGM analysis:",
        err.response?.data || err.message
      );
      alert(
        err.response?.data?.details ||
          "Failed to retrieve AI CGM analysis. Patient may not have processed CGM data."
      );
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        "https://app.metabolytics.ai/simulator-backend/healthcare/api/admin/users",
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setUsers(res.data);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  const fetchHealthcareProviders = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        "https://app.metabolytics.ai/simulator-backend/healthcare/api/admin/healthcare-providers",
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setHealthcareProviders(res.data);
    } catch (err) {
      console.error("Error fetching healthcare providers:", err);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        "https://app.metabolytics.ai/simulator-backend/healthcare/api/admin/organizations",
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setOrganizations(res.data);
    } catch (err) {
      console.error("Error fetching organizations:", err);
    }
  };

  const deleteOrganization = async (orgId) => {
    if (!orgId) {
      console.error("❌ Error: Organization ID is undefined.");
      alert("Error: Cannot delete organization. Invalid ID.");
      return;
    }

    const confirmDelete = window.confirm(
      "Are you sure you want to delete this organization?"
    );
    if (!confirmDelete) return;

    try {
      console.log("📢 Sending delete request for organization ID:", orgId);
      const token = localStorage.getItem("token");

      await axios.delete(
        `https://app.metabolytics.ai/simulator-backend/healthcare/api/admin/organizations/${orgId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      console.log("✅ Organization deleted successfully:", orgId);
      setOrganizations(organizations.filter((org) => org.identifier !== orgId));
    } catch (err) {
      console.error(
        "❌ Error deleting organization:",
        err.response?.data || err.message
      );
      alert("Failed to delete organization.");
    }
  };

  const viewDetails = (organization) => {
    console.log("📢 Viewing organization details:", organization); // ✅ Debugging log
    setSelectedOrganization(organization);
    setOpenDetailsDialog(true);
  };

  useEffect(() => {
    fetchPatients();
  }, []);

  // Fetch Patients

  const fetchPatients = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        "https://app.metabolytics.ai/simulator-backend/patients/api/patients",
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setPatients(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error fetching patients:", err);
    }
  };

  // ✅ Define function to open patient details modal
  const handleViewDetails = (patient) => {
    setSelectedPatient(patient);
    setOpenPatientDialog(true);
  };

  // ✅ Define function to close modal
  const handleClose = () => {
    setOpenPatientDialog(false);
    setSelectedPatient(null);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  const handleRoleFilter = (event) => {
    setSelectedRole(event.target.value);
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  const filteredUsers = users.filter(
    (user) =>
      (selectedRole === "" || user.role === selectedRole) &&
      (searchQuery === "" ||
        user.email.includes(searchQuery) ||
        user.firstName.includes(searchQuery) ||
        user.lastName.includes(searchQuery))
  );

  const handleShowUserDetails = (user) => {
    setSelectedUser(user);
    setOpenUserDetailsDialog(true); // Changed from setOpenUserDialog
  };

  const handleShowProviderDetails = (provider) => {
    setSelectedProvider(provider);
    setOpenProviderDialog(true);
  };

  //Add new user
  const addUser = async () => {
    if (
      !newUser.firstName ||
      !newUser.lastName ||
      !newUser.email ||
      !newUser.role ||
      !newUser.password ||
      (newUser.role !== "Admin" && !newUser.healthcareProvider)
    ) {
      setErrorMessage("All fields (including password) are required.");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const userData = {
        firstName: newUser.firstName,
        lastName: newUser.lastName,
        email: newUser.email,
        password: newUser.password,
        role: newUser.role,
        profile: {
          organization:
            newUser.role !== "Admin" ? newUser.healthcareProvider : null,
        },
      };

      console.log("Sending user data:", userData);

      const res = await axios.post(
        "https://app.metabolytics.ai/simulator-backend/healthcare/api/admin/users",
        userData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      setUsers([...users, res.data]);
      setOpenAddUserDialog(false);
      setNewUser({
        firstName: "",
        lastName: "",
        email: "",
        role: "",
        healthcareProvider: "",
        password: "",
      });
      setErrorMessage("");
      alert("User added successfully!");

      // Refresh the users list
      fetchUsers();
    } catch (err) {
      console.error("❌ Error adding user:", err.response?.data || err.message);
      setErrorMessage(
        err.response?.data?.details || "Failed to add user. Please try again."
      );
    }
  };

  const addHealthcareProvider = async () => {
    if (
      !newProvider.name ||
      !newProvider.tax_id ||
      !newProvider.contact.email ||
      !newProvider.contact.phone
    ) {
      setSnackbarMessage("All provider fields are required");
      setSnackbarOpen(true);
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        "https://app.metabolytics.ai/simulator-backend/healthcare/api/admin/healthcare-providers",
        newProvider,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setHealthcareProviders([...healthcareProviders, res.data]);
      setOpenProviderDialog(false);
      setSnackbarMessage("Healthcare provider added successfully");
      setSnackbarOpen(true);
    } catch (err) {
      console.error("Error adding healthcare provider:", err);
    }
  };

  const deleteUser = async (userId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this user?"
    );
    if (!confirmDelete) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `https://app.metabolytics.ai/simulator-backend/healthcare/api/admin/users/${userId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setUsers(users.filter((user) => user._id !== userId));
    } catch (err) {
      console.error("Error deleting user:", err);
    }
  };

  const deleteHealthcareProvider = async (providerId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this healthcare provider?"
    );
    if (!confirmDelete) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `https://app.metabolytics.ai/simulator-backend/healthcare/api/admin/healthcare-providers/${providerId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setHealthcareProviders(
        healthcareProviders.filter((provider) => provider._id !== providerId)
      );
    } catch (err) {
      console.error("Error deleting healthcare provider:", err);
    }
  };

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        Admin Dashboard
      </Typography>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        marginBottom={2}
      >
        <Button variant="contained" onClick={() => setOpenAddUserDialog(true)}>
          {" "}
          {/* Changed this line */}
          Add User
        </Button>
        <Button variant="contained" onClick={() => setOpenProviderDialog(true)}>
          Add Healthcare Provider
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={handleOpenOnboardingDialog}
        >
          Onboard Organization
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={handleOpenPractitionerOnboardingDialog}
        >
          Onboard Practitioners
        </Button>

        <Button variant="contained" color="secondary" onClick={handleLogout}>
          Logout
        </Button>
      </Box>

      {/* Single Add User Dialog */}
      <Dialog
        open={openAddUserDialog}
        onClose={() => {
          setOpenAddUserDialog(false);
          setErrorMessage("");
          setNewUser({
            firstName: "",
            lastName: "",
            email: "",
            role: "",
            healthcareProvider: "",
            password: "",
          });
        }}
      >
        <DialogTitle>Add New User</DialogTitle>
        <DialogContent>
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}

          <TextField
            label="First Name"
            value={newUser.firstName}
            onChange={(e) =>
              setNewUser({ ...newUser, firstName: e.target.value })
            }
            fullWidth
            margin="dense"
          />
          <TextField
            label="Last Name"
            value={newUser.lastName}
            onChange={(e) =>
              setNewUser({ ...newUser, lastName: e.target.value })
            }
            fullWidth
            margin="dense"
          />
          <TextField
            label="Email"
            type="email"
            value={newUser.email}
            onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
            fullWidth
            margin="dense"
          />

          <TextField
            label="Password"
            type="password"
            value={newUser.password}
            onChange={(e) =>
              setNewUser({ ...newUser, password: e.target.value })
            }
            fullWidth
            margin="dense"
          />

          <FormControl fullWidth margin="dense">
            <InputLabel>Role</InputLabel>
            <Select
              value={newUser.role}
              onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
            >
              <MenuItem value="Physician">Physician</MenuItem>
              <MenuItem value="Registered Nurse">Registered Nurse</MenuItem>
              <MenuItem value="Nutritionist">Nutritionist</MenuItem>
              <MenuItem value="Admin">Admin</MenuItem>
            </Select>
          </FormControl>

          {newUser.role !== "Admin" && (
            <FormControl fullWidth margin="dense">
              <InputLabel>Healthcare Provider</InputLabel>
              <Select
                value={newUser.healthcareProvider}
                onChange={(e) =>
                  setNewUser({ ...newUser, healthcareProvider: e.target.value })
                }
              >
                {healthcareProviders.map((provider) => (
                  <MenuItem key={provider._id} value={provider._id}>
                    {provider.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={addUser} color="primary">
            Add
          </Button>
          <Button onClick={() => setOpenAddUserDialog(false)} color="secondary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add New Healthcare Organization */}
      <Dialog
        open={openProviderDialog}
        onClose={() => setOpenProviderDialog(false)}
      >
        <DialogTitle>Add Healthcare Provider</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            onChange={(e) =>
              setNewProvider({ ...newProvider, name: e.target.value })
            }
            fullWidth
          />
          <TextField
            label="Tax ID"
            onChange={(e) =>
              setNewProvider({ ...newProvider, tax_id: e.target.value })
            }
            fullWidth
          />
          <TextField
            label="Email"
            onChange={(e) =>
              setNewProvider({
                ...newProvider,
                contact: { ...newProvider.contact, email: e.target.value },
              })
            }
            fullWidth
          />
          <TextField
            label="Phone"
            onChange={(e) =>
              setNewProvider({
                ...newProvider,
                contact: { ...newProvider.contact, phone: e.target.value },
              })
            }
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={addHealthcareProvider} color="primary">
            Add
          </Button>
          <Button
            onClick={() => setOpenProviderDialog(false)}
            color="secondary"
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* EPIC Organization's Practitioners Onboarding Dialog */}
      <Dialog
        open={openPractitionerOnboardingDialog}
        onClose={() => setOpenPractitionerOnboardingDialog(false)}
      >
        <DialogTitle>Select Organization to Onboard Practitioners</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Select Organization from EPIC</InputLabel>
            <Select
              value={selectedOrgForPractitioners}
              onChange={(e) => setSelectedOrgForPractitioners(e.target.value)}
            >
              {epicOrganizations.map((org) => (
                <MenuItem key={org.identifier} value={org.identifier}>
                  {org.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography sx={{ mt: 2 }} color="textSecondary">
            This will onboard all practitioners associated with the selected
            organization.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenPractitionerOnboardingDialog(false)}
            color="secondary"
          >
            Cancel
          </Button>
          <Button onClick={handleOnboardPractitioners} color="primary">
            Onboard All Practitioners
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modified onboarding dialog */}
      <Dialog
        open={openOnboardingDialog}
        onClose={() => setOpenOnboardingDialog(false)}
      >
        <DialogTitle>Select Organization for Onboarding</DialogTitle>
        <DialogContent>
          <FormControl fullWidth>
            <InputLabel>Select Organization from EPIC</InputLabel>
            <Select
              value={selectedOrganization}
              onChange={(e) => setSelectedOrganization(e.target.value)}
            >
              {epicOrganizations.map((org) => (
                <MenuItem key={org.identifier} value={org.identifier}>
                  {org.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenOnboardingDialog(false)}
            color="secondary"
          >
            Cancel
          </Button>
          <Button onClick={handleOnboard} color="primary">
            Confirm Onboarding
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openGroomingDialog}
        onClose={() => setOpenGroomingDialog(false)}
      >
        <DialogTitle>Patient Grooming Data</DialogTitle>
        <DialogContent>
          <pre>{JSON.stringify(groomingData, null, 2)}</pre>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenGroomingDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openCGMDialog} onClose={() => setOpenCGMDialog(false)}>
        <DialogTitle>Patient CGM Data</DialogTitle>
        <DialogContent>
          <pre>{JSON.stringify(cgmData, null, 2)}</pre>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCGMDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Typography variant="h5" gutterBottom sx={{ marginTop: 4 }}>
        Practitioners
      </Typography>

      <FormControl sx={{ minWidth: 200, marginRight: 2 }}>
        <InputLabel>Filter by Role</InputLabel>
        <Select value={selectedRole} onChange={handleRoleFilter}>
          <MenuItem key="all" value="">
            All
          </MenuItem>
          <MenuItem key="physician" value="Physician">
            Physician
          </MenuItem>
          <MenuItem key="nurse" value="Registered Nurse">
            Registered Nurse
          </MenuItem>
          <MenuItem key="assistant" value="Medical Assistant">
            Medical Assistant
          </MenuItem>
          <MenuItem key="admin" value="Admin">
            Admin
          </MenuItem>
        </Select>
      </FormControl>

      <TextField
        label="Search Users"
        variant="outlined"
        onChange={handleSearch}
        sx={{ marginBottom: 2 }}
      />

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>First Name</TableCell>
              <TableCell>Last Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Organization</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map((user) => (
              <TableRow key={user._id}>
                <TableCell key={`name-${user._id}`}>{user.firstName}</TableCell>
                <TableCell key={`lastname-${user._id}`}>
                  {user.lastName}
                </TableCell>
                <TableCell key={`email-${user._id}`}>{user.email}</TableCell>
                <TableCell key={`role-${user._id}`}>{user.role}</TableCell>
                <TableCell key={`org-${user._id}`}>
                  {user.profile?.organization}
                </TableCell>
                <TableCell key={`actions-${user._id}`}>
                  <Button
                    variant="contained"
                    onClick={() => handleShowUserDetails(user)}
                  >
                    View Details
                  </Button>
                </TableCell>
                <TableCell key={`delete-${user._id}`}>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={() => deleteUser(user._id)}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h5" gutterBottom sx={{ marginTop: 4 }}>
        Organizations
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Identifier</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {organizations.map((org) => (
              <TableRow key={org.identifier}>
                <TableCell>{org.identifier}</TableCell>
                <TableCell>{org.name}</TableCell>
                <TableCell>{org.type}</TableCell>
                <TableCell>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => viewDetails(org)}
                  >
                    View Details
                  </Button>
                </TableCell>
                <TableCell>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={() => {
                      console.log(
                        "🛑 Deleting organization with ID:",
                        org.identifier
                      ); // Debugging
                      deleteOrganization(org.identifier);
                    }}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h5" gutterBottom sx={{ marginTop: 4 }}>
        Healthcare Providers
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Tax ID</TableCell>
              <TableCell>Contact Email</TableCell>
              <TableCell>Phone</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {healthcareProviders.map((provider) => (
              <TableRow key={provider._id}>
                <TableCell key={`name-${provider._id}`}>
                  {provider.name}
                </TableCell>
                <TableCell key={`tax-${provider._id}`}>
                  {provider.tax_id}
                </TableCell>
                <TableCell key={`email-${provider._id}`}>
                  {provider.contact.email}
                </TableCell>
                <TableCell key={`phone-${provider._id}`}>
                  {provider.contact.phone}
                </TableCell>
                <TableCell key={`actions-${provider._id}`}>
                  <Button
                    variant="contained"
                    onClick={() => handleShowProviderDetails(provider)}
                  >
                    View Details
                  </Button>
                </TableCell>
                <TableCell key={`delete-${provider._id}`}>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={() => deleteHealthcareProvider(provider._id)}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Patients  Data Section */}
      <Typography variant="h5" gutterBottom sx={{ marginTop: 4 }}>
        Patients
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Full Name</TableCell>
              <TableCell>Age</TableCell>
              <TableCell>Diabetes Type</TableCell>
              <TableCell>CGM Usage</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {patients.map((patient) => (
              <TableRow key={patient._id}>
                <TableCell key={`name-${patient._id}`}>
                  {patient?.name || "N/A"}
                </TableCell>
                <TableCell key={`age-${patient._id}`}>
                  {patient?.age || "N/A"}
                </TableCell>
                <TableCell key={`type-${patient._id}`}>
                  {patient?.type || "N/A"}
                </TableCell>
                <TableCell key={`cgm-${patient._id}`}>
                  {patient?.glucose_monitoring?.using_cgm || "No"}
                </TableCell>
                <TableCell key={`details-${patient._id}`}>
                  <Button
                    variant="contained"
                    onClick={() => handleViewDetails(patient)}
                  >
                    View Details
                  </Button>
                </TableCell>
                <TableCell key={`grooming-${patient._id}`}>
                  <Button
                    variant="contained"
                    onClick={() => handleViewGrooming(patient)}
                  >
                    Grooming Data
                  </Button>
                </TableCell>
                <TableCell key={`cgmdata-${patient._id}`}>
                  <Button
                    variant="contained"
                    onClick={() => handleViewCGM(patient)}
                  >
                    CGM Data
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Organization Details Dialog */}
      <Dialog
        open={openDetailsDialog}
        onClose={() => setOpenDetailsDialog(false)}
      >
        <DialogTitle>Organization Details</DialogTitle>
        <DialogContent>
          {selectedOrganization ? (
            <div>
              <Typography>
                <strong>Identifier:</strong>{" "}
                {selectedOrganization.identifier || "N/A"}
              </Typography>
              <Typography>
                <strong>Name:</strong> {selectedOrganization.name || "N/A"}
              </Typography>
              <Typography>
                <strong>Type:</strong> {selectedOrganization.type || "N/A"}
              </Typography>
              <Typography>
                <strong>HQ Location:</strong>{" "}
                {selectedOrganization.hq_location?.city || "N/A"},{" "}
                {selectedOrganization.hq_location?.state || "N/A"}
              </Typography>
              <Typography>
                <strong>Contact:</strong>{" "}
                {selectedOrganization.contact?.email || "N/A"}
              </Typography>
            </div>
          ) : (
            <Typography>No organization details available.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetailsDialog(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Details Dialog */}
      <Dialog
        open={openUserDetailsDialog}
        onClose={() => setOpenUserDetailsDialog(false)}
      >
        <DialogTitle>User Details</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <div>
              <Typography>
                <strong>Name:</strong> {selectedUser.firstName}{" "}
                {selectedUser.lastName}
              </Typography>
              <Typography>
                <strong>Email:</strong> {selectedUser.email}
              </Typography>
              <Typography>
                <strong>Role:</strong> {selectedUser.role}
              </Typography>
              <Typography>
                <strong>Healthcare Provider:</strong>{" "}
                {selectedUser.healthcareProvider?.name || "N/A"}
              </Typography>
              <Typography>
                <strong>Created At:</strong>{" "}
                {new Date(selectedUser.createdAt).toLocaleDateString()}
              </Typography>
              {/* Add any other user details you want to display */}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenUserDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Healthcare Provider Details Dialog */}
      <Dialog
        open={openProviderDialog}
        onClose={() => setOpenProviderDialog(false)}
      >
        <DialogTitle>Healthcare Provider Details</DialogTitle>
        <DialogContent>
          {selectedProvider && (
            <pre>{JSON.stringify(selectedProvider, null, 2)}</pre>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenProviderDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Patient Grooming Data Modal */}
      <Dialog open={openPatientDialog} onClose={handleClose}>
        <DialogTitle>Patient Grooming Data</DialogTitle>
        <DialogContent>
          {selectedPatient && (
            <pre>{JSON.stringify(selectedPatient, null, 2)}</pre>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default AdminDashboard;
